import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Barcode from 'react-barcode';
import axios from 'axios';
import DOMPurify from 'dompurify';
import './PrintWorkTicket.css';

const colorMap = {
  '#FFA500': 'ORANGE',
  '#FF0000': 'RED',
  '#00FF00': 'GREEN',
  '#0000FF': 'BLUE',
  '#FFFFFF': 'WHITE',
  '#000000': 'BLACK',
};
const getColorName = (value) => {
  if (!value) return 'N/A';
  const upperValue = String(value).trim().toUpperCase();
  if (colorMap[upperValue]) {
    return colorMap[upperValue];
  }
  return upperValue;
};

const formatTeethRange = (teethString) => {
  if (!teethString || teethString.trim() === '') return '';
  const teethArray = teethString.split(',').map(t => t.trim()).filter(t => t !== '');
  if (teethArray.length === 0) return '';
  const numbers = teethArray.map(t => parseInt(t)).filter(n => !isNaN(n));
  if (numbers.length === 0) return teethString;
  numbers.sort((a, b) => a - b);
  const ranges = [];
  let start = numbers[0];
  let end = numbers[0];
  for (let i = 1; i < numbers.length; i++) {
    if (numbers[i] === end + 1) {
      end = numbers[i];
    } else {
      if (start === end) {
        ranges.push(start.toString());
      } else {
        ranges.push(start + '-' + end);
      }
      start = numbers[i];
      end = numbers[i];
    }
  }
  if (start === end) {
    ranges.push(start.toString());
  } else {
    ranges.push(start + '-' + end);
  }
  return ranges.join(', ');
};

const countTeeth = (teethString) => {
  if (!teethString || typeof teethString !== 'string' || teethString.trim() === '') {
    return 1;
  }
  const teethList = teethString.split(',').map((item) => item.trim());
  let totalTeeth = 0;
  teethList.forEach((item) => {
    if (item.includes('-')) {
      const [start, end] = item
        .split('-')
        .map((num) => parseInt(num.trim(), 10))
        .filter((num) => !isNaN(num));
      if (start && end && start <= end) {
        totalTeeth += end - start + 1;
      }
    } else {
      if (!isNaN(parseInt(item, 10))) {
        totalTeeth += 1;
      }
    }
  });
  return totalTeeth > 0 ? totalTeeth : 1;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatDateWithDay = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const dayName = date.toLocaleString('en-US', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `${dayName} ${day} ${month} ${year}`;
};

const getNoteContent = (note) => {
  let content = note.editorHtml || note.text || "";
  if (!content) return "N/A";

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    // Check if doc.body exists before accessing innerHTML
    if (!doc || !doc.body) {
      // Fallback: strip HTML tags manually if DOMParser fails
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      content = tempDiv.textContent || tempDiv.innerText || content;
      return content.trim() || "N/A";
    }

    doc.querySelectorAll("script,style").forEach(el => el.remove());
    doc.querySelectorAll("*").forEach((el) => {
      const text = el.textContent.trim().toLowerCase();
      if (
        text.startsWith("date:") ||
        text.startsWith("from:") ||
        text.startsWith("for:") ||
        text.startsWith("status:") ||
        text.startsWith("received:") ||
        text.startsWith("case status") ||
        text.startsWith("# status") ||
        text.startsWith("# received") ||
        text.startsWith("created by") ||
        text.startsWith("created on") ||
        text.startsWith("updated by") ||
        text.startsWith("updated on")
      ) {
        el.remove();
      }
    });

    let cleaned = doc.body.innerHTML;
    cleaned = cleaned
      .replace(/Date\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/From\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/For\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Status\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Received\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Created By\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Created On\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Updated By\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "")
      .replace(/Updated On\s*:.*?(\<br\s*\/?>|\<\/p>|\n|$)/gi, "");
    cleaned = cleaned.trim();
    return cleaned || "N/A";
  } catch (err) {
    console.error('Error parsing note content:', err);
    // Fallback: return plain text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv.textContent?.trim() || "N/A";
  }
};

const PrintWorkTicket = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [providerName, setProviderName] = useState('N/A');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`);
        setCaseData(response.data);
      } catch (err) {
        setError('Error fetching case details: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchCaseDetails();
  }, [caseId]);

  useEffect(() => {
    if (caseData) {
      setProviderName(caseData.provider_name || 'N/A');
    }
  }, [caseData]);

  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!caseData?.account_holder) return;
      try {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/account/${caseData.account_holder}`);
        setAccountDetails(response.data);
      } catch (error) {
        console.error('Error fetching account details:', error);
        setAccountDetails(null);
      }
    };
    fetchAccountDetails();
  }, [caseData]);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!caseId) return;
      try {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/notes`);
        let allNotes = [];
        if (response.data.notes && Array.isArray(response.data.notes) && response.data.notes.length > 0) {
          allNotes = response.data.notes;
        }
        else if (Array.isArray(response.data)) {
          allNotes = response.data;
        }
        else if (response.data.editorHtml) {
          allNotes.push({
            editorHtml: response.data.editorHtml,
            text: response.data.text || '',
            isInternal: false
          });
        }
        else if (response.data.text) {
          allNotes.push({
            text: response.data.text,
            isInternal: false
          });
        }
        const externalNotes = allNotes.filter(note => {
          if (note.isInternal === true) return false;
          return true;
        });
        const sortedExternalNotes = externalNotes.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateA - dateB;
        });
        let selectedNotes = [];
        if (sortedExternalNotes.length > 0) {
          selectedNotes.push(sortedExternalNotes[0]);
          if (sortedExternalNotes.length > 1) {
            selectedNotes.push(sortedExternalNotes[sortedExternalNotes.length - 1]);
          }
        }
        setNotes(selectedNotes);
      } catch (error) {
        console.error('Error fetching notes:', error);
        setNotes([]);
      }
    };
    fetchNotes();
  }, [caseId]);

  const handleBack = () => {
    navigate(`/case/${caseId}`);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!caseData) return <p>No case data found</p>;

  const stripHtml = (html) =>
    DOMPurify.sanitize(html, { ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'div', 'ul', 'li'] }) || 'N/A';

  const getUnitCount = (procedure) => {
    if (procedure.calculatedUnits !== undefined && procedure.calculatedUnits !== null) {
      return procedure.calculatedUnits;
    }
    if (procedure.procedure && procedure.procedure.toLowerCase().includes('postage')) {
      return 1;
    }
    if (procedure.teeth && procedure.teeth.length > 0) {
      if (Array.isArray(procedure.teeth)) {
        return procedure.teeth.length;
      }
      if (typeof procedure.teeth === 'string') {
        return countTeeth(procedure.teeth);
      }
    }
    return 1;
  };

  const getFormattedTeeth = (procedure) => {
    if (!procedure.teeth) return '';
    let teethString = '';
    if (Array.isArray(procedure.teeth)) {
      teethString = procedure.teeth.join(', ');
    } else if (typeof procedure.teeth === 'string') {
      teethString = procedure.teeth;
    } else {
      return '';
    }
    return formatTeethRange(teethString);
  };

  const currentDateTime = new Date().toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });

  const ticketData = {
    caseNumber: caseData.caseNumber || 'N/A',
    panColor: getColorName(caseData.color),
    account: {
      name: accountDetails?.account_name || 'N/A',
      address: accountDetails?.address || '',
      cityState: `${accountDetails?.city || ''}, ${accountDetails?.state || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|\s*,$/g, '').trim(),
      zip: accountDetails?.zip || '',
      phone: accountDetails?.telephone || 'N/A',
      fax: accountDetails?.fax || 'N/A',
    },
    patient: `${caseData.first_name || ''} ${caseData.last_name || ''}`.trim() || 'N/A',
    status: caseData.status || 'N/A',
    received: caseData.date
      ? formatDateWithDay(caseData.date)
      : 'N/A',
    workRequested: caseData.procedures
      ? caseData.procedures.map((proc, index) => ({
        unit: getUnitCount(proc),
        description: proc.procedure || 'N/A',
        teeth: getFormattedTeeth(proc),
        shade: proc.shade || 'N/A',
        material: proc.alloyMaterial || proc.alley || 'N/A',
        fullDescription: `${index + 1} - ${proc.procedure || 'N/A'}${proc.teeth && proc.teeth.length > 0 ? ` - Teeth: ${getFormattedTeeth(proc)}` : ''}`,
        status: `Shade: ${proc.shade || 'N/A'}, Material: ${proc.alloyMaterial || proc.alley || 'N/A'}`,
      }))
      : [],
    provider: providerName,
    delivery: caseData.delivery_date
      ? formatDateWithDay(caseData.delivery_date)
      : 'N/A',
    patient2: `${caseData.first_name || ''} ${caseData.last_name || ''}`.trim() || 'N/A',
    notes: notes.map((note) => getNoteContent(note)),
  };

  return (
    <div
      className="print-work-ticket-container"
      style={{
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        maxWidth: '100%',
        padding: '8px 12px',
        margin: '0',
        fontSize: '12px',
        color: '#000'
      }}
    >
      {/* Action Buttons */}
      <div className="d-flex justify-content-start mb-4 no-print">
        <Button
          variant="primary"
          style={{ backgroundColor: '#004aad', borderColor: '#004aad', marginRight: '8px' }}
          onClick={() => window.print()}
        >
          Print
        </Button>
        <Button
          variant="secondary"
          onClick={handleBack}
        >
          Back
        </Button>
      </div>

      {/* Print Header */}
      <div
        className="print-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          position: "relative",
          marginTop: "0px",
          marginBottom: "10px",
          fontSize: "12px",
        }}
      >
        <div>{currentDateTime}</div>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: '500',
          }}
        >
          HMS - Hira Management System
        </div>
        <div></div>
      </div>

      {/* Two-column Layout */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'flex-start'
        }}
      >
        {/* Left Section */}
        <div
          style={{
            width: '65%',
            borderRight: '1px solid #d3d3d3',
            paddingRight: '10px'
          }}
        >
          {/* Case Number + Pan Color, and Barcode top-right */}
          <div
            className="d-flex justify-content-between align-items-start mb-3"
            style={{ width: '100%' }}
          >
            <div
              className="case-header"
              style={{ marginTop: '-10px' }}
            >
              <h2 style={{ fontSize: '25px', marginBottom: '0', fontWeight: 'bolder' }}>CASE #{ticketData.caseNumber}</h2>
              <h4 style={{ fontSize: '20px', fontWeight: 'bolder' }}>PAN COLOR: {ticketData.panColor}</h4>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              marginTop: '-10px'
            }}>
              <div>
                <Barcode
                  value={ticketData.caseNumber}
                  format="CODE128"
                  height={30}
                  width={1.2}
                  displayValue={false}
                />
              </div>
            </div>
          </div>

          {/* Account & Provider */}
          <div className="row mb-3 print-two-col">
            <div className="col-md-7 account-column.column" style={{ paddingRight: '15px', whiteSpace: 'nowrap' }}>
              <h5 style={{ fontWeight: 'bold', fontSize: '20px' }}>Account</h5>
              <div style={{ paddingLeft: "20px" }}>
                <div
                  className="account-name"
                  style={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    lineHeight: "1.5"
                  }}
                >
                  {ticketData.account.name.toUpperCase()}
                </div>
                <div className="account-print-data account-details">
                  {ticketData.account.address && (
                    <div>{ticketData.account.address}</div>
                  )}
                  {(ticketData.account.cityState || ticketData.account.zip) && (
                    <div>
                      {[ticketData.account.cityState, ticketData.account.zip].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                <p className="mb-1 account-print-data account-details">
                  {ticketData.account.phone}
                </p>
              </div>
            </div>
            <div className="col-md-5" style={{ paddingLeft: '0px' }}>
              <h5 style={{ fontWeight: 'bold', fontSize: '20px', marginLeft: '-30px' }}>Provider</h5>
              <p
                style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  marginLeft: '-20px'
                }}
              >
                {ticketData.provider}
              </p>
            </div>
          </div>

          {/* Patient & Delivery */}
          <div className="row mb-3 print-two-col">
            <div className="col-md-7 account-column.column" style={{ paddingRight: '15px' }}>
              <h5 style={{ fontWeight: 'bold', fontSize: '20px' }}>Patient</h5>
              <p
                style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  paddingLeft: '15px'
                }}
              >
                {ticketData.patient}
              </p>
            </div>

            <div className="col-md-5" style={{ paddingLeft: '0px' }}>
              <h5
                style={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                  marginLeft: '-30px'
                }}
              >
                Delivery
              </h5>

              <p
                style={{
                  marginTop: '8px',
                  fontSize: '15px',
                  whiteSpace: 'nowrap',
                  marginLeft: '-20px'
                }}
              >
                {ticketData.delivery}
              </p>
            </div>
          </div>

          {/* Status & Quality Check */}
          <div
            className="row mb-3 print-two-col"
            style={{ alignItems: 'flex-start' }}
          >
            <div
              className="col-md-7 account-column.column"
              style={{ paddingRight: '15px' }}
            >
              <h5 style={{ fontWeight: 'bold', fontSize: '20px' }}>Status</h5>

              <p
                style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  paddingLeft: '15px'
                }}
              >
                {ticketData.status}
              </p>
            </div>

            <div className="col-md-5" style={{ paddingLeft: '0px' }}>
              <h5
                style={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                  marginLeft: '-30px'
                }}
              >
                Quality Check
              </h5>

              <div
                style={{
                  marginTop: '8px',
                  marginLeft: '-20px',
                  marginBottom: '10px',
                  padding: '4px 8px',
                  border: '1px solid #000',
                  borderRadius: '4px',
                  fontSize: '10px',
                  boxSizing: 'border-box',
                  width: '160px',
                  backgroundColor: 'transparent',
                  paddingLeft: '15px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '3px',
                    alignItems: 'center'
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Completed By:
                  </span>

                  <div
                    style={{
                      borderBottom: '1px solid #000',
                      width: '50px',
                      height: '12px'
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '3px',
                    alignItems: 'center'
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    Checked By:
                  </span>

                  <div
                    style={{
                      borderBottom: '1px solid #000',
                      width: '50px',
                      height: '12px'
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    Packed By:
                  </span>

                  <div
                    style={{
                      borderBottom: '1px solid #000',
                      width: '50px',
                      height: '12px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Received - Row 4 */}
          <div className="row mb-3 print-two-col">
            <div className="col-md-7" style={{ paddingRight: '15px' }}>
              <h5 style={{ fontWeight: 'bold', fontSize: '20px' }}>Received</h5>
              <p style={{ marginTop: '8px', fontSize: '14px', paddingLeft: '15px' }}>{ticketData.received}</p>
            </div>
            <div className="col-md-5" style={{ paddingLeft: '15px' }}>
              {/* Empty for spacing */}
            </div>
          </div>

          {/* Work Requested Table */}
          <div className="mb-4 work-requested-section">
            <h5 style={{ fontWeight: 'bold', fontSize: '12px' }}>Work Requested</h5>
            <table
              className="table table-bordered work-requested-table"
              style={{
                marginBottom: 0,
                borderCollapse: 'collapse',
                fontSize: '11px',
                lineHeight: '1'
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: '5%',
                      padding: '1px 3px',
                      lineHeight: '12px',
                      fontSize: '14px'
                    }}
                  >
                    Unit
                  </th>
                  <th
                    style={{
                      width: '95%',
                      padding: '1px 3px',
                      lineHeight: '12px',
                      fontSize: '14px'
                    }}
                  >
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {ticketData.workRequested.length > 0 ? (
                  ticketData.workRequested.map((item, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          verticalAlign: 'middle',
                          padding: '1px 3px',
                          lineHeight: '12px',
                          height: '18px'
                        }}
                      >
                        {item.unit}
                      </td>
                      <td
                        className="description-cell"
                        style={{
                          padding: '1px 3px',
                          lineHeight: '12px',
                          height: '18px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {item.description}
                        {item.teeth && item.teeth.length > 0 && (
                          <span style={{ color: '#000', marginLeft: '5px', display: 'inline' }}>
                            {item.teeth && ` - Teeth: ${item.teeth}`}
                            {item.shade && item.shade !== 'N/A' && ` - Shade: ${item.shade}`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" style={{ textAlign: 'center' }}>No procedures requested</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Section - Visible on Screen */}
          <div className="footer-screen">
            <div
              style={{
                marginTop: '10px',
                paddingTop: '8px',
                fontSize: '8px',
                lineHeight: '1.2',
                color: '#333',
                fontFamily: 'Arial, sans-serif',
                width: '100%',
                textAlign: 'justify',
                borderTop: '1px solid #ddd'
              }}
            >
              {/* <FooterContent /> */}
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div
          style={{
            width: '28%',
            paddingLeft: '20px',
            paddingTop: '-20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginTop: '-25px'
          }}
        >
          {/* Notes */}
          <div style={{ width: '100%', textAlign: 'left', marginTop: '35px' }}>
            {ticketData.notes && ticketData.notes.length > 0 ? (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#333',
                    marginBottom: '4px',
                    paddingBottom: '2px'
                  }}>
                    Note:
                  </div>
                  <div className="notes-content"
                    dangerouslySetInnerHTML={{
                      __html: stripHtml(ticketData.notes[0]),
                    }}
                  />
                </div>
                {ticketData.notes.length > 1 && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd' }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#333',
                      marginBottom: '4px',
                      paddingBottom: '2px'
                    }}>
                    </div>
                    <div className="notes-content"
                      dangerouslySetInnerHTML={{
                        __html: stripHtml(ticketData.notes[1]),
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <p>No external notes available</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer Section - Visible Only When Printing */}
      <div className="footer-print" style={{ display: 'none' }}>
        <div
          style={{
            marginTop: '5px',
            paddingTop: '2px',
            fontSize: '8px',
            lineHeight: '1',
            color: '#333',
            fontFamily: 'Arial, sans-serif',
            width: '100%',
            textAlign: 'justify'
          }}
        >
          {/* <FooterContent /> */}
        </div>
      </div>
    </div>
  );
};

export default PrintWorkTicket;